import { useMutation, useQuery } from '@tanstack/react-query'

import { converObjectErrorInString, convertObjectToCamelCase, convertObjectToSnakeCase } from '@/helpers'

import { QUERIES_ADMIN } from './api.constants'
import { api } from './api.service'

import type {
  HistoryWithPagesProps,
  OrderCenterCreditClassificationProps,
  OrderCenterFinanceClassificationProps,
  OrderCenterLogisticClassificationProps,
  SaleOrderCommentsProps,
  SaleOrderCommentsWithPagesProps,
  SaleOrderCreditWithPagesProps,
  SaleOrderFinanceWithPagesProps,
  SaleOrderLogisticWithPagesProps,
  SaleOrderProps,
  SaleOrderSupplyOrdersResponse,
  SaleOrderWithPagesProps,
  UpdateSupplyOrderOnSaleOrderCancelPayload,
} from '@/types'

import {
  BFF_SALE_ORDER_CANCELLATION_UPDATE_SUPPLY_ORDERS_URL,
  BFF_SALE_ORDER_SUPPLY_ORDERS_DELETE_URL,
  BFF_SALE_ORDER_SUPPLY_ORDERS_URL,
  BFF_SALE_ORDERS_URL,
  ORDERS_CENTER_COMMENTS_URL,
  ORDERS_CENTER_CREDIT_CLASSIFICATION_URL,
  ORDERS_CENTER_CREDIT_URL,
  ORDERS_CENTER_FINANCE_CLASSIFICATION_URL,
  ORDERS_CENTER_FINANCE_URL,
  ORDERS_CENTER_LOGISTIC_CLASSIFICATION_URL,
  ORDERS_CENTER_LOGISTIC_GENERATE_DELIVERY_NOTE_URL,
  ORDERS_CENTER_LOGISTIC_URL,
  ORDERS_CENTER_ORDER_CENTER_HISTORIES_URL,
  ORDERS_CENTER_PROFILE_URL,
  ORDERS_CENTER_SALE_ORDER_URL,
  ORDERS_CENTER_SALE_ORDERS_URL,
  SALE_ORDER_BULK_URL,
} from './api.urls'

export const useOrderCenterProfileList = (
  params: Record<string, string | number>,
  isEnabled = true,
) => {
  const getOrderProfileList = async (): Promise<{
    success: boolean
    message: string
    data?: SaleOrderWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      const offset = copyParams?.page
        ? (+(copyParams?.page || 0) - 1) * +copyParams.limit
        : 0

      delete copyParams.page

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key] && copyParams?.[key] !== 'undefined') {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(ORDERS_CENTER_PROFILE_URL, {
        params: {
          ...(convertObjectToSnakeCase(formattedParams) as Record<
            string,
            string
          >),
          offset,
        },
      })

      return {
        success: true,
        message: 'Order requests fetched successfully!',
        data: convertObjectToCamelCase(data) as SaleOrderWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get order requests!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, 'profile', params],
    queryFn: () => getOrderProfileList(),
    enabled: isEnabled,
  })
}

export const useOrderCenterCreditList = (
  params: Record<string, string | number>,
  isEnabled = true,
) => {
  const getOrderCreditList = async (): Promise<{
    success: boolean
    message: string
    data?: SaleOrderCreditWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      const offset = copyParams?.page
        ? (+(copyParams?.page || 0) - 1) * +copyParams.limit
        : 0

      delete copyParams.page

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key] && copyParams?.[key] !== 'undefined') {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(ORDERS_CENTER_CREDIT_URL, {
        params: {
          ...(convertObjectToSnakeCase(formattedParams) as Record<
            string,
            string
          >),
          offset,
        },
      })

      return {
        success: true,
        message: 'Order credit fetched successfully!',
        data: convertObjectToCamelCase(data) as SaleOrderCreditWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get order credit!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, 'credit', params],
    queryFn: () => getOrderCreditList(),
    enabled: isEnabled,
  })
}

export const useOrderCenterFinanceList = (
  params: Record<string, string | number>,
  isEnabled = true,
) => {
  const getOrderFinanceList = async (): Promise<{
    success: boolean
    message: string
    data?: SaleOrderFinanceWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      const offset = copyParams?.page
        ? (+(copyParams?.page || 0) - 1) * +copyParams.limit
        : 0

      delete copyParams.page

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key] && copyParams?.[key] !== 'undefined') {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(ORDERS_CENTER_FINANCE_URL, {
        params: {
          ...(convertObjectToSnakeCase(formattedParams) as Record<
            string,
            string
          >),
          offset,
        },
      })

      return {
        success: true,
        message: 'Order finance fetched successfully!',
        data: convertObjectToCamelCase(data) as SaleOrderFinanceWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get order finance!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, 'finance', params],
    queryFn: () => getOrderFinanceList(),
    enabled: isEnabled,
  })
}

export const useOrderCenterFinanceClassification = (
  filter?: Record<string, string | number>,
  isEnabled = true,
) => {
  const getOrderLogisticClassification = async (): Promise<{
    success: boolean
    message: string
    data?: OrderCenterFinanceClassificationProps
  }> => {
    try {
      const { data } = await api.get(ORDERS_CENTER_FINANCE_CLASSIFICATION_URL, {
        params: {
          ...(convertObjectToSnakeCase(filter) as Record<string, string>),
        },
      })

      return {
        success: true,
        message: 'Order finance classification fetched successfully!',
        data: convertObjectToCamelCase(
          data,
        ) as OrderCenterFinanceClassificationProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get order finance classification!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [
      QUERIES_ADMIN.ORDER_CENTER.module,
      'finance-classification',
      filter,
    ],
    queryFn: () => getOrderLogisticClassification(),
    enabled: isEnabled,
  })
}

export const useOrderCenterLogisticList = (
  params: Record<string, string | number>,
  isEnabled = true,
) => {
  const getOrderLogisticList = async (): Promise<{
    success: boolean
    message: string
    data?: SaleOrderLogisticWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      const offset = copyParams?.page
        ? (+(copyParams?.page || 0) - 1) * +copyParams.limit
        : 0

      delete copyParams.page

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key] && copyParams?.[key] !== 'undefined') {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(ORDERS_CENTER_LOGISTIC_URL, {
        params: {
          ...(convertObjectToSnakeCase(formattedParams) as Record<
            string,
            string
          >),
          offset,
        },
      })

      return {
        success: true,
        message: 'Order logistic fetched successfully!',
        data: convertObjectToCamelCase(data) as SaleOrderLogisticWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get order logistic!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, 'logistic', params],
    queryFn: () => getOrderLogisticList(),
    enabled: isEnabled,
  })
}

export const useOrderCenterLogisticClassification = (
  filter?: Record<string, string | number>,
  isEnabled = true,
) => {
  const getOrderLogisticClassification = async (): Promise<{
    success: boolean
    message: string
    data?: OrderCenterLogisticClassificationProps
  }> => {
    try {
      const { data } = await api.get(
        ORDERS_CENTER_LOGISTIC_CLASSIFICATION_URL,
        {
          params: {
            ...(convertObjectToSnakeCase(filter) as Record<string, string>),
          },
        },
      )

      return {
        success: true,
        message: 'Order logistic classification fetched successfully!',
        data: convertObjectToCamelCase(
          data,
        ) as OrderCenterLogisticClassificationProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get order logistic classification!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [
      QUERIES_ADMIN.ORDER_CENTER.module,
      'logistic-classification',
      filter,
    ],
    queryFn: () => getOrderLogisticClassification(),
    enabled: isEnabled,
  })
}

export const useSaleOrderVariantList = (
  params: Record<string, string | number>,
  isEnabled = true,
) => {
  const getOrderSaleOrderList = async (): Promise<{
    success: boolean
    message: string
    data?: SaleOrderWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      const offset = copyParams?.page
        ? (+(copyParams?.page || 0) - 1) * +copyParams.limit
        : 0

      delete copyParams.page
      delete copyParams.currentPage

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key] && copyParams?.[key] !== 'undefined') {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(BFF_SALE_ORDERS_URL, {
        params: {
          ...(formattedParams as Record<string, string>),
          offset,
        },
      })

      return {
        success: true,
        message: 'Order sale order fetched successfully!',
        data: convertObjectToCamelCase(data) as SaleOrderWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get order sale order!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, 'sale-order', params],
    queryFn: () => getOrderSaleOrderList(),
    enabled: isEnabled,
  })
}

export const useOrderCenterSaleOrderList = (
  params: Record<string, string | number>,
  isEnabled = true,
) => {
  const getOrderSaleOrderList = async (): Promise<{
    success: boolean
    message: string
    data?: SaleOrderWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      const offset = copyParams?.page
        ? (+(copyParams?.page || 0) - 1) * +copyParams.limit
        : 0

      delete copyParams.page
      delete copyParams.currentPage

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key] && copyParams?.[key] !== 'undefined') {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(ORDERS_CENTER_SALE_ORDERS_URL, {
        params: {
          ...(convertObjectToSnakeCase(formattedParams) as Record<
            string,
            string
          >),
          offset,
        },
      })

      return {
        success: true,
        message: 'Order sale order fetched successfully!',
        data: convertObjectToCamelCase(data) as SaleOrderWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get order sale order!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, 'sale-order', params],
    queryFn: () => getOrderSaleOrderList(),
    enabled: isEnabled,
  })
}

export const useOrderCenterSaleOrderDetail = (uuid?: string) => {
  const getOrderProfileDetail = async (): Promise<{
    success: boolean
    message: string
    data?: SaleOrderProps
  }> => {
    try {
      const { data } = await api.get(ORDERS_CENTER_SALE_ORDER_URL(uuid || ''))

      return {
        success: true,
        message: 'Sale order fetched successfully!',
        data: convertObjectToCamelCase(data) as SaleOrderProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get sale order!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, uuid],
    queryFn: () => getOrderProfileDetail(),
    enabled: !!uuid,
  })
}

export const useOrderCenterSaleOrderUpdate = (uuid?: string) => {
  const updateOrder = async (
    payload: Partial<SaleOrderProps>,
  ): Promise<{
    success: boolean
    message: string
    data?: SaleOrderProps
  }> => {
    try {
      const { data } = await api.patch(
        ORDERS_CENTER_SALE_ORDER_URL(`${uuid}`),
        convertObjectToSnakeCase(payload),
      )

      return {
        success: true,
        message: 'Sale order updated successfully!',
        data: convertObjectToCamelCase(data) as SaleOrderProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to update sale order!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.ORDER_CENTER.module, uuid],
    mutationFn: (payload: Partial<SaleOrderProps>) => updateOrder(payload),
  })
}

export const useOrderCenterSaleOrderRemove = () => {
  const deleteOrder = async (
    uuid: string,
  ): Promise<{
    success: boolean
    message: string
    data?: SaleOrderProps
  }> => {
    try {
      const { data } = await api.delete(ORDERS_CENTER_SALE_ORDER_URL(`${uuid}`))

      return {
        success: true,
        message: 'Sale order deleted successfully!',
        data: convertObjectToCamelCase(data) as SaleOrderProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to delete sale order!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.ORDER_CENTER.module],
    mutationFn: (uuid: string) => deleteOrder(uuid),
  })
}

export const useOrderCenterLogisticGenerateRemission = () => {
  const orderCenterLogisticGenerateRemission = async (
    uuid: string,
  ): Promise<{
    success: boolean
    message: string
    data?: Blob
  }> => {
    try {
      const { data } = await api.get<Blob>(
        ORDERS_CENTER_LOGISTIC_GENERATE_DELIVERY_NOTE_URL(`${uuid}`),
        {
          responseType: 'blob',
        },
      )

      return {
        success: true,
        message: 'Successfully!',
        data,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [
      QUERIES_ADMIN.ORDER_CENTER_LOGISTIC_GENERATE_REMISSION.module,
    ],
    mutationFn: (uuid: string) => orderCenterLogisticGenerateRemission(uuid),
  })
}

export const useOrderCenterCommentsList = (
  uuid?: string,
  params?: Record<string, string | number>,
  isEnabled = true,
) => {
  const getOrderCommentsList = async (): Promise<{
    success: boolean
    message: string
    data?: SaleOrderCommentsWithPagesProps
  }> => {
    try {
      const formattedParams = Object.keys(params || {}).reduce((acc, key) => {
        if (params?.[key] && params?.[key] !== 'undefined') {
          return {
            ...acc,
            [key]: params?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(ORDERS_CENTER_COMMENTS_URL(`${uuid}`), {
        params: {
          ...(convertObjectToSnakeCase(formattedParams) as Record<
            string,
            string
          >),
        },
      })

      return {
        success: true,
        message: 'Order comments fetched successfully!',
        data: convertObjectToCamelCase(data) as SaleOrderCommentsWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get order comments!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.ORDER_CENTER_COMMENTS.module],
    queryFn: () => getOrderCommentsList(),
    enabled: isEnabled,
  })
}

export const useOrderCenterCommentsCreate = (uuid?: string) => {
  const orderCenterCommentsCreate = async (payload: {
    type?: string
    subject?: string
    comment: string
  }): Promise<{
    success: boolean
    message: string
    data?: SaleOrderCommentsProps
  }> => {
    try {
      const { data } = await api.post(
        ORDERS_CENTER_COMMENTS_URL(`${uuid}`),
        convertObjectToSnakeCase(payload),
      )

      return {
        success: true,
        message: 'Successfully!',
        data,
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      let message = ''

      if (err?.response?.data) {
        message = converObjectErrorInString(err?.response?.data) || message
      }
      return {
        success: false,
        message,
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.ORDER_CENTER_COMMENTS.module],
    mutationFn: (payload: {
      type?: string
      subject?: string
      comment: string
    }) => orderCenterCommentsCreate(payload),
  })
}

export const useOrderCenterCommentsEdit = (saleOrderUuid: string) => {
  const orderCenterCommentsEdit = async (payload: {
    uuid?: string
    comment: string
  }): Promise<{
    success: boolean
    message: string
    data?: SaleOrderCommentsProps
  }> => {
    try {
      const { data } = await api.patch(
        `${ORDERS_CENTER_COMMENTS_URL(`${saleOrderUuid}`)}${payload?.uuid}/`,
        convertObjectToSnakeCase(payload),
      )

      return {
        success: true,
        message: 'Successfully!',
        data,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.ORDER_CENTER_COMMENTS.module],
    mutationFn: (payload: { uuid?: string; comment: string }) =>
      orderCenterCommentsEdit(payload),
  })
}

export const useOrderCenterCommentRemove = (saleOrderUuid: string) => {
  const deleteOrderComment = async (
    uuid: string,
  ): Promise<{
    success: boolean
    message: string
    data?: SaleOrderCommentsProps
  }> => {
    try {
      const { data } = await api.delete(
        `${ORDERS_CENTER_COMMENTS_URL(`${saleOrderUuid}`)}${uuid}/`,
      )

      return {
        success: true,
        message: 'Sale order comment deleted successfully!',
        data: convertObjectToCamelCase(data) as SaleOrderCommentsProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to delete sale order comment!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.ORDER_CENTER_COMMENTS.module],
    mutationFn: (uuid: string) => deleteOrderComment(uuid),
  })
}

export const useOrderCenterHistoryList = (
  uuid?: string,
  params?: Record<string, string | number>,
) => {
  const getOrderHistoryList = async (): Promise<{
    success: boolean
    message: string
    data?: HistoryWithPagesProps
  }> => {
    try {
      const formattedParams = Object.keys(params || {}).reduce((acc, key) => {
        if (params?.[key] && params?.[key] !== 'undefined') {
          return {
            ...acc,
            [key]: params?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(
        ORDERS_CENTER_ORDER_CENTER_HISTORIES_URL(`${uuid}`),
        {
          params: {
            ...(convertObjectToSnakeCase(formattedParams) as Record<
              string,
              string
            >),
          },
        },
      )

      return {
        success: true,
        message: 'Order histories fetched successfully!',
        data: convertObjectToCamelCase(data) as HistoryWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get order histories!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.ORDER_CENTER_HISTORIES.module],
    queryFn: () => getOrderHistoryList(),
    enabled: !!uuid,
  })
}

export const useBulkSaleOrder = () => {
  const bulkSaleOrder = async (payload: {
    items: string[]
    data: Partial<SaleOrderProps>
  }): Promise<{
    success: boolean
    message: string
    data?: SaleOrderProps
  }> => {
    try {
      const { data } = await api.patch(
        SALE_ORDER_BULK_URL,
        convertObjectToSnakeCase(payload),
      )

      return {
        success: true,
        message: 'bulk successfully!',
        data: convertObjectToCamelCase(data) as SaleOrderProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to bulk!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.ORDER_CENTER_BULK.module],
    mutationFn: (payload: { items: string[]; data: Partial<SaleOrderProps> }) =>
      bulkSaleOrder(payload),
  })
}

export const useOrderCenterCreditClassification = (
  filter?: Record<string, string | number>,
  isEnabled = true,
) => {
  const getOrderCreditClassification = async (): Promise<{
    success: boolean
    message: string
    data?: OrderCenterCreditClassificationProps
  }> => {
    try {
      const { data } = await api.get(ORDERS_CENTER_CREDIT_CLASSIFICATION_URL, {
        params: {
          ...(convertObjectToSnakeCase(filter) as Record<string, string>),
        },
      })

      return {
        success: true,
        message: 'Order credit classification fetched successfully!',
        data: convertObjectToCamelCase(
          data,
        ) as OrderCenterCreditClassificationProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get order credit classification!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [
      QUERIES_ADMIN.ORDER_CENTER.module,
      'credit-classification',
      filter,
    ],
    queryFn: () => getOrderCreditClassification(),
    enabled: isEnabled,
  })
}

export const useSaleOrderSupplyOrders = (uuid?: string, isEnabled: boolean = true) => {
  const getSaleOrderSupplyOrders = async (): Promise<{
    success: boolean;
    message: string;
    data?: SaleOrderSupplyOrdersResponse
  }> => {
    try {
      const { data } = await api.get(
        BFF_SALE_ORDER_SUPPLY_ORDERS_URL(`${uuid}`)
      );

      return {
        success: true,
        message: "Supply Order fetched successfully!",
        data: convertObjectToCamelCase(data) as SaleOrderSupplyOrdersResponse,
      };
    } catch (err) {
      return {
        success: false,
        message: "Failed to get Supply Order!",
        data: undefined,
      };
    }
  };

  return useQuery({
    queryKey: [QUERIES_ADMIN.BFF_SALE_ORDER_SUPPLY_ORDERS_URL.module, uuid],
    queryFn: () => getSaleOrderSupplyOrders(),
    enabled: !!uuid && isEnabled,
    refetchOnWindowFocus: false,
  })
}

export const useUpdateSupplyOrdersOnSaleOrderCancel = (uuid?: string) => {
  const updateSupplyOrdersOnSaleOrderCancel = async (payload: UpdateSupplyOrderOnSaleOrderCancelPayload): Promise<{
    success: boolean
    message: string
    data?: null
  }> => {
    try {
      const { data } = await api.post(
        BFF_SALE_ORDER_CANCELLATION_UPDATE_SUPPLY_ORDERS_URL(uuid || ''),
        payload,
      )

      return {
        success: true,
        message: 'successfully!',
        data: convertObjectToCamelCase(data) as null,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.UPDATE_SUPPLY_ORDER_ON_SALE_ORDER_CANCEL.module],
    mutationFn: (payload: UpdateSupplyOrderOnSaleOrderCancelPayload) =>
      updateSupplyOrdersOnSaleOrderCancel(payload),
  })
}

export const useSaleOrderDeleteSupplyOrderVinculed = () => {
  const saleOrderDeleteSupplyOrderVinculed = async (
    uuid: string,
    supplyOrderUuid: string,
  ): Promise<{
    success: boolean
    message: string
    data?: null
  }> => {
    try {
      const { data } = await api.delete(`${BFF_SALE_ORDER_SUPPLY_ORDERS_DELETE_URL(uuid, supplyOrderUuid)}`);

      return {
        success: true,
        message: 'Supply order deleted successfully!',
        data: convertObjectToCamelCase(data) as null,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to deleted supply order!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.BFF_SALE_ORDER_SUPPLY_ORDERS_DELETE_URL.module],
    mutationFn: ({ uuid, supplyOrderUuid }: { uuid: string, supplyOrderUuid: string }) => saleOrderDeleteSupplyOrderVinculed(uuid, supplyOrderUuid),
  })
}
